import React, { createContext, useContext, useRef } from 'react';
import useSocketConnection from '../hooks/useSocketConnection';

const ConnectionContext = createContext();

export const useConnection = () => {
  const context = useContext(ConnectionContext);
  if (!context) {
    throw new Error('useConnection must be used within ConnectionProvider');
  }
  return context;
};

export const ConnectionProvider = ({ 
  children, 
  roomId, 
  nickname,
  gameStateActions, 
  playerActions, 
  gameObjectsActions, 
  animationActions,
  audioActions 
}) => {
  const initializeRef = useRef(false);

  // Combine all the actions for the socket connection hook
  const gameStateHandlers = React.useMemo(() => ({
    setGameState: gameStateActions.setGameState,
    setConnectionError: gameStateActions.setConnectionError,
    setSuddenDeath: gameStateActions.setSuddenDeath,
    setMyPlayerId: playerActions.setMyPlayerId,
    setPlayers: playerActions.setPlayers,
    setObservers: playerActions.setObservers,
    setBombs: gameObjectsActions.setBombs,
    setExplosions: gameObjectsActions.setExplosions,
    setWalls: gameObjectsActions.setWalls,
    setUpgrades: gameObjectsActions.setUpgrades,
    setIsObserver: playerActions.setIsObserver,
    setWinner: gameStateActions.setWinner,
    setGameOverCountdown: gameStateActions.setGameOverCountdown,
    setLobbyCountdown: gameStateActions.setLobbyCountdown,
    setGameTimer: gameStateActions.setGameTimer,
    setPlayerSpawnCorners: playerActions.setPlayerSpawnCorners,
    setSelectedMap: gameStateActions.setSelectedMap,
    setAvailableMaps: gameStateActions.setAvailableMaps,
    myPlayerIdRef: playerActions.myPlayerIdRef,
    myUniquePlayerIdRef: playerActions.myUniquePlayerIdRef,
    playerStateBuffers: animationActions.playerStateBuffers,
    bombStateBuffers: animationActions.bombStateBuffers,
    playerAnimationState: animationActions.playerAnimationState,
    initializeRef
  }), [gameStateActions, playerActions, gameObjectsActions, animationActions]);

  const storageHandlers = React.useMemo(() => ({
    getStoredPlayerId: playerActions.getStoredPlayerId,
    setStoredPlayerId: playerActions.setStoredPlayerId,
    getStoredUniquePlayerId: playerActions.getStoredUniquePlayerId,
    setStoredUniquePlayerId: playerActions.setStoredUniquePlayerId
  }), [playerActions]);

  // Use the existing socket connection hook
  const {
    socketRef,
    emitPlayerInput,
    emitKickBomb,
    emitPlaceBomb,
    emitToggleReady,
    emitJoinAsPlayer,
    emitBecomeObserver,
    emitGetAvailableMaps,
    emitChangeMap
  } = useSocketConnection(
    roomId,
    gameStateHandlers,
    storageHandlers,
    audioActions,
    nickname
  );

  // Connection actions
  const actions = React.useMemo(() => ({
    emitPlayerInput,
    emitKickBomb,
    emitPlaceBomb,
    emitToggleReady,
    emitJoinAsPlayer,
    emitBecomeObserver,
    emitGetAvailableMaps,
    emitChangeMap,
    
    // Connection utilities
    isConnected: () => socketRef.current?.connected || false,
    disconnect: () => {
      if (socketRef.current?.connected) {
        socketRef.current.disconnect();
      }
    },
    reconnect: () => {
      if (socketRef.current) {
        socketRef.current.connect();
      }
    }
  }), [socketRef, emitPlayerInput, emitKickBomb, emitPlaceBomb, emitToggleReady, emitJoinAsPlayer, emitBecomeObserver, emitGetAvailableMaps, emitChangeMap]);

  // Memoized state values
  const state = React.useMemo(() => ({
    socketRef
  }), [socketRef]);

  const value = React.useMemo(() => ({
    ...state,
    ...actions
  }), [state, actions]);

  return (
    <ConnectionContext.Provider value={value}>
      {children}
    </ConnectionContext.Provider>
  );
};

export default ConnectionContext;